import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, Activity, Clock, AlertTriangle, ChevronRight, LogOut,
  User, RefreshCw, Loader2, FileText, Heart
} from 'lucide-react';

const COLORS = {
  primary: '#1A237E',
  primaryLight: '#3949AB',
  success: '#4CAF50',
  danger: '#F44336',
  warning: '#F9A825',
  white: '#FFFFFF',
  background: '#F0F2F5',
  text: '#212121',
  textLight: '#757575',
  gray: '#9E9E9E',
  grayLight: '#E0E0E0',
};

const NurseDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [patients, setPatients] = useState([]);
  const [adherenceOverview, setAdherenceOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/auth'); return; }
    const u = JSON.parse(stored);
    setUser(u);
    loadData(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (u) => {
    setLoading(true);
    try {
      const role = u.role;
      const id = u.id;

      // Load patients
      const patientsUrl = role === 'admin' 
        ? `${API}/api/users/admin/${id}/elders`
        : `${API}/api/users/caregiver/${id}/elders`;
      const pRes = await fetch(patientsUrl);
      if (pRes.ok) {
        const pData = await pRes.json();
        setPatients(pData);
      }

      // Load adherence overview
      const adhUrl = role === 'admin'
        ? `${API}/api/adherence/admin/${id}/overview`
        : `${API}/api/adherence/nurse/${id}/overview`;
      const aRes = await fetch(adhUrl);
      if (aRes.ok) {
        const aData = await aRes.json();
        setAdherenceOverview(aData);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    await loadData(user);
    setRefreshing(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/');
  };

  const getAdherenceColor = (rate) => {
    if (rate >= 80) return COLORS.success;
    if (rate >= 50) return COLORS.warning;
    return COLORS.danger;
  };

  const getAdherenceLabel = (rate) => {
    if (rate >= 80) return 'Good';
    if (rate >= 50) return 'Fair';
    return 'Needs Attention';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.background }}>
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4" style={{ color: COLORS.primary }} size={48} />
          <p style={{ color: COLORS.textLight }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const patientReports = adherenceOverview?.patient_reports || [];
  const summaryStats = adherenceOverview?.summary || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* Header */}
      <header className="sticky top-0 z-50 shadow-md" style={{ backgroundColor: COLORS.primary }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
              <User size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg" data-testid="dashboard-user-name">{user?.name || 'Dashboard'}</h1>
              <p className="text-white/70 text-xs capitalize">{user?.role === 'admin' ? 'Family Admin' : 'Nurse / Caregiver'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleRefresh} disabled={refreshing} className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="refresh-btn">
              <RefreshCw size={20} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout} className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="logout-btn">
              <LogOut size={20} className="text-white" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          {[
            { icon: Users, label: 'Patients', value: patients.length, color: COLORS.primary },
            { icon: Activity, label: 'Avg Adherence', value: `${summaryStats.average_adherence || 0}%`, color: COLORS.success },
            { icon: AlertTriangle, label: 'Needs Attention', value: summaryStats.patients_needing_attention || 0, color: COLORS.warning },
            { icon: Clock, label: 'Active Schedules', value: patientReports.reduce((a, p) => a + (p.missed_doses || 0), 0), color: COLORS.danger },
          ].map((stat, i) => (
            <div key={stat.label} className="bg-white rounded-xl p-4 shadow-sm" data-testid={`stat-card-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}>
                  <stat.icon size={20} style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold" style={{ color: COLORS.text }}>{stat.value}</p>
              <p className="text-sm" style={{ color: COLORS.textLight }}>{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Patient List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="patient-list">
          <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${COLORS.grayLight}` }}>
            <h2 className="font-bold text-lg" style={{ color: COLORS.text }}>Patients</h2>
            <Link to="/adherence-reports" className="text-sm font-medium flex items-center gap-1 hover:underline" style={{ color: COLORS.primary }} data-testid="view-reports-link">
              <FileText size={16} /> View Reports
            </Link>
          </div>

          {patients.length === 0 ? (
            <div className="p-8 text-center" data-testid="no-patients-message">
              <Users className="mx-auto mb-3" style={{ color: COLORS.grayLight }} size={48} />
              <p style={{ color: COLORS.textLight }}>No patients assigned yet.</p>
              <p className="text-sm mt-1" style={{ color: COLORS.gray }}>Patients will appear here once assigned by an admin.</p>
            </div>
          ) : (
            <div>
              {patients.map((p, idx) => {
                const patientUser = p.user || p;
                const patientId = patientUser.id || patientUser.user_id;
                const report = patientReports.find(r => r.patient_id === patientId);
                const rate = report?.adherence_rate ?? null;

                return (
                  <div key={patientId || idx} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    style={{ borderBottom: idx < patients.length - 1 ? `1px solid ${COLORS.grayLight}` : 'none' }}
                    data-testid={`patient-row-${idx}`}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${COLORS.primary}12` }}>
                      <User size={24} style={{ color: COLORS.primary }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate" style={{ color: COLORS.text }}>{patientUser.name || 'Patient'}</p>
                      <p className="text-sm" style={{ color: COLORS.textLight }}>
                        {p.profile?.medical_conditions?.length ? p.profile.medical_conditions.join(', ') : 'No conditions recorded'}
                      </p>
                    </div>
                    {rate !== null && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold" style={{ color: getAdherenceColor(rate) }}>{rate}%</p>
                        <p className="text-xs" style={{ color: getAdherenceColor(rate) }}>{getAdherenceLabel(rate)}</p>
                      </div>
                    )}
                    <ChevronRight size={20} style={{ color: COLORS.grayLight }} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4" data-testid="quick-actions">
          <Link to="/adherence-reports" className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-3 hover:shadow-md transition-shadow" data-testid="action-reports">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS.primary}12` }}>
              <FileText size={24} style={{ color: COLORS.primary }} />
            </div>
            <div>
              <p className="font-semibold" style={{ color: COLORS.text }}>Adherence Reports</p>
              <p className="text-xs" style={{ color: COLORS.textLight }}>View detailed reports</p>
            </div>
          </Link>
          <Link to="/pricing" className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-3 hover:shadow-md transition-shadow" data-testid="action-plans">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS.success}12` }}>
              <Heart size={24} style={{ color: COLORS.success }} />
            </div>
            <div>
              <p className="font-semibold" style={{ color: COLORS.text }}>Subscription Plans</p>
              <p className="text-xs" style={{ color: COLORS.textLight }}>Manage your plan</p>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default NurseDashboard;
